import React, { useState, useEffect } from 'react';
import { InputBase, Paper, IconButton, MenuItem, Select, FormControl, useTheme } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import './SearchBar.css';

type Props = {
  onSearch: (query: { type: string, value: string }) => void;
  menuItems: { label: string, value: string }[];
  className?: string;
};

const SearchBar = ({ onSearch, menuItems, className = '' }: Props) => {
  const theme = useTheme();
  const [searchType, setSearchType] = useState(menuItems.length > 0 ? menuItems[0].value : '');
  const [searchPlaceholder, setSearchPlaceholder] = useState(`Search by ${menuItems.length > 0 ? menuItems[0].label : ''}`);
  const [query, setQuery] = useState('');
  const [typingTimeout, setTypingTimeout] = useState<NodeJS.Timeout | null>(null);

  // Auto-search when query changes
  useEffect(() => {
    if (typingTimeout) {
      clearTimeout(typingTimeout);
    }

    const timeout = setTimeout(() => {
      onSearch({ type: searchType, value: query });
    }, 2000);

    setTypingTimeout(timeout);

    return () => {
      if (typingTimeout) {
        clearTimeout(typingTimeout);
      }
    };
  }, [query, searchType]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    onSearch({ type: searchType, value: query });
  };

  return (
    <Paper 
      component="form" 
      className={`search-bar ${className}`}
      onSubmit={handleSubmit}
      sx={{
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        maxWidth: '500px',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        border: `1px solid ${theme.palette.divider}`,
        '&:hover': {
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        },
      }}
    >
      <FormControl 
        variant="standard" 
        sx={{
          m: 0,
          minWidth: 120,
          '& .MuiInput-underline:before': {
            borderBottom: 'none',
          },
          '& .MuiInput-underline:after': {
            borderBottom: 'none',
          },
        }}
      >
        <Select
          value={searchType}
          onChange={(e) => {
            setSearchType(e.target.value);
            setSearchPlaceholder(`Search by ${menuItems.find(item => item.value === e.target.value)?.label || ''}`);
          }}
          disableUnderline
          sx={{
            padding: '8px 16px',
            fontWeight: 500,
            '& .MuiSelect-select': {
              paddingRight: '24px !important',
            },
          }}
          MenuProps={{
            PaperProps: {
              style: {
                marginTop: '8px',
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              },
            },
          }}
        >
          {menuItems?.map((item) => (
            <MenuItem key={item.value} value={item.value}>
              {item.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <div style={{ flex: 1, position: 'relative' }}>
        <InputBase
          className="input"
          placeholder={searchPlaceholder}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          inputProps={{ 'aria-label': 'search' }}
          sx={{
            width: '100%',
            padding: '8px 16px',
            '& input': {
              padding: '4px 0',
            },
          }}
        />
      </div>
      
      <IconButton 
        type="submit" 
        aria-label="search"
        sx={{
          padding: '8px',
          color: theme.palette.primary.main,
        }}
      >
        <SearchIcon />
      </IconButton>
    </Paper>
  );
};

export default SearchBar;
